import type { SalesforceContext } from "@ai-sales/contracts";
import type { SessionChannel } from "./api.js";

const contextObjectLabels: Record<string, string> = {
  Account: "客户",
  Contact: "联系人",
  Lead: "潜在客户",
  Opportunity: "业务机会",
  Task: "跟进任务",
  Sales_Visit__c: "销售拜访",
  AI_Quote_Request__c: "报价资料"
};

export type ContextSourceMode = "page" | "conversation";

export function salesforceObjectLabel(objectApiName?: string): string {
  return objectApiName
    ? contextObjectLabels[objectApiName] ?? objectApiName
    : "全局工作模式";
}

export function selectEffectiveContext(
  liveContext: Partial<SalesforceContext>,
  storedContext: Partial<SalesforceContext> | undefined,
  channel: SessionChannel | undefined,
  sourceMode: ContextSourceMode = "page"
): Partial<SalesforceContext> {
  // The development channel simulates the Salesforce utility entry locally.
  // Page mode prevents an old record from leaking into a later list/global page.
  // Conversation mode is explicit: users can keep a queried/action-result record
  // while navigating elsewhere, and can switch back to the live page at any time.
  if (channel === "salesforce" || channel === "development") {
    return sourceMode === "conversation" ? storedContext ?? liveContext : liveContext;
  }
  return storedContext ?? liveContext;
}

export function describeSalesforceContext(context: Partial<SalesforceContext>): {
  objectLabel: string;
  contextLabel: string;
} {
  const objectLabel = salesforceObjectLabel(context.objectApiName);
  if (!context.recordId) {
    return {
      objectLabel,
      contextLabel: context.objectApiName
        ? `${objectLabel} · 列表模式`
        : "可直接查询、创建或更新，无需先打开记录"
    };
  }
  return {
    objectLabel,
    contextLabel: context.recordLabel ?? `${objectLabel} · 当前记录`
  };
}

export function describeContextFocus(
  context: Partial<SalesforceContext>,
  sourceMode: ContextSourceMode
): { prefix: string; label: string } | undefined {
  if (!context.recordId) return undefined;
  const objectLabel = salesforceObjectLabel(context.objectApiName);
  return {
    prefix: sourceMode === "conversation" ? "已固定" : "当前记录",
    label: context.recordLabel ?? objectLabel
  };
}
